import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SealCheck, UploadSimple, IdentificationCard, ClockCountdown, XCircle } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

function FileUpload({ label, url, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <div className="border-2 border-dashed border-black/10 rounded-2xl p-6 text-center hover:border-gold transition-colors">
      <Label className="block text-xs tracking-widest uppercase text-neutral-500 mb-3">{label}</Label>
      {url ? (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-emerald-700">
            <SealCheck size={20} weight="fill" /><span className="text-sm">Uploaded</span>
          </div>
          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-gold underline">View file</a>
          <button onClick={() => onChange(null)} className="block mx-auto text-xs text-red-700">Replace</button>
        </div>
      ) : (
        <>
          <UploadSimple size={28} className="text-gold mx-auto mb-3" />
          <input type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" id={testId} data-testid={testId} disabled={uploading} />
          <label htmlFor={testId} className="inline-block px-4 py-2 rounded-full bg-midnight text-bone text-xs tracking-wide cursor-pointer hover:bg-midnight-hover">
            {uploading ? "Uploading..." : "Choose file"}
          </label>
          <p className="text-xs text-neutral-500 mt-2">JPG, PNG or PDF · max 8MB</p>
        </>
      )}
    </div>
  );
}

export default function VerificationPage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [aadhaar, setAadhaar] = useState("");
  const [aadhaarFrontUrl, setAadhaarFrontUrl] = useState(null);
  const [aadhaarBackUrl, setAadhaarBackUrl] = useState(null);
  const [voterUrl, setVoterUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [otpMode, setOtpMode] = useState({ enabled: false, provider: null });
  const [otpStep, setOtpStep] = useState(0); // 0: idle, 1: waiting OTP, 2: verified
  const [otpRef, setOtpRef] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);

  useEffect(() => {
    api.get("/verifications/mine").then(({ data }) => {
      setCurrent(data);
      if (data.aadhaar_number) setAadhaar(data.aadhaar_number);
      if (data.aadhaar_front_url) setAadhaarFrontUrl(data.aadhaar_front_url);
      else if (data.aadhaar_url) setAadhaarFrontUrl(data.aadhaar_url); // legacy fallback
      if (data.aadhaar_back_url) setAadhaarBackUrl(data.aadhaar_back_url);
      if (data.voter_id_url) setVoterUrl(data.voter_id_url);
    });
    api.get("/kyc/aadhaar/status").then(({ data }) => setOtpMode({ enabled: !!data.otp_enabled, provider: data.provider })).catch(() => {});
  }, []);

  const sendOtp = async () => {
    if (!/^\d{12}$/.test(aadhaar)) { toast.error("Enter 12-digit Aadhaar first"); return; }
    setOtpBusy(true);
    try {
      const { data } = await api.post("/kyc/aadhaar/send-otp", { aadhaar_number: aadhaar, consent: true });
      setOtpRef(data.reference_id);
      setOtpStep(1);
      toast.success(`OTP sent via ${data.provider} to Aadhaar-linked mobile`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send OTP");
    } finally { setOtpBusy(false); }
  };

  const verifyOtp = async () => {
    if (!/^\d{4,8}$/.test(otpCode)) { toast.error("OTP must be 4–8 digits"); return; }
    setOtpBusy(true);
    try {
      const { data } = await api.post("/kyc/aadhaar/verify-otp", { reference_id: otpRef, otp: otpCode });
      toast.success(`✅ Verified as ${data.name || "Aadhaar holder"}!`);
      setOtpStep(2);
      const { data: mine } = await api.get("/verifications/mine");
      setCurrent(mine);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "OTP verification failed");
    } finally { setOtpBusy(false); }
  };

  const submit = async () => {
    if (!/^\d{12}$/.test(aadhaar)) { toast.error("Aadhaar must be 12 digits"); return; }
    if (!aadhaarFrontUrl) { toast.error("Upload Aadhaar FRONT side"); return; }
    if (!aadhaarBackUrl) { toast.error("Upload Aadhaar BACK side"); return; }
    setSubmitting(true);
    try {
      await api.post("/verifications", {
        aadhaar_number: aadhaar,
        aadhaar_front_url: aadhaarFrontUrl,
        aadhaar_back_url: aadhaarBackUrl,
        voter_id_url: voterUrl,
      });
      toast.success("Verification submitted — Admin will review shortly");
      const { data } = await api.get("/verifications/mine");
      setCurrent(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSubmitting(false); }
  };

  const status = current?.status || "not_submitted";
  const statusUi = {
    approved: { icon: SealCheck, color: "text-emerald-700 bg-emerald-50", label: "Verified" },
    pending: { icon: ClockCountdown, color: "text-amber-700 bg-amber-50", label: "Under review" },
    rejected: { icon: XCircle, color: "text-red-700 bg-red-50", label: "Rejected — resubmit" },
    not_submitted: { icon: IdentificationCard, color: "text-neutral-600 bg-stone", label: "Not submitted" },
  }[status];
  const StatusIcon = statusUi.icon;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Trust & Safety</p>
      <h1 className="font-serif text-4xl text-midnight mb-3">ID Verification</h1>
      <p className="text-neutral-600 mb-8 leading-relaxed">
        Verify your identity to unlock rentals, list properties, and build trust with the Zyro community.
        Your documents are private and encrypted.
      </p>

      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${statusUi.color} mb-8`} data-testid="verify-status">
        <StatusIcon size={18} weight="fill" /><span>{statusUi.label}</span>
      </div>

      {status === "approved" ? (
        <div className="p-8 bg-emerald-50 border border-emerald-200 rounded-2xl">
          <SealCheck size={40} weight="fill" className="text-emerald-600 mb-3" />
          <p className="font-serif text-xl text-emerald-900 mb-2">You're verified!</p>
          <p className="text-sm text-emerald-800">You can now rent rooms, list properties, and access all Zyro features.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">Aadhaar Number</Label>
            <Input
              inputMode="numeric" maxLength={12} value={aadhaar}
              onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, "").slice(0, 12))}
              placeholder="12-digit Aadhaar" data-testid="aadhaar-input"
            />
          </div>

          {otpMode.enabled && otpStep < 2 && (
            <div className="p-5 rounded-2xl border-2 border-emerald-200 bg-emerald-50" data-testid="otp-verify-box">
              <p className="font-medium text-emerald-900 mb-1">⚡ Instant Aadhaar OTP Verification</p>
              <p className="text-xs text-emerald-800 mb-4">
                Verify instantly via OTP on your Aadhaar-linked mobile — no image upload needed. Powered by {otpMode.provider}.
              </p>
              {otpStep === 0 && (
                <Button type="button" onClick={sendOtp} disabled={otpBusy || aadhaar.length !== 12}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-aadhaar-otp-btn">
                  {otpBusy ? "Sending..." : "📨 Send OTP to Aadhaar-linked mobile"}
                </Button>
              )}
              {otpStep === 1 && (
                <div className="space-y-3">
                  <Input
                    inputMode="numeric" maxLength={8} value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                    placeholder="Enter 4-8 digit OTP" data-testid="aadhaar-otp-input"
                    className="tracking-widest text-center font-mono text-lg"
                  />
                  <div className="flex gap-2">
                    <Button type="button" onClick={verifyOtp} disabled={otpBusy}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="verify-aadhaar-otp-btn">
                      {otpBusy ? "Verifying..." : "✅ Verify OTP"}
                    </Button>
                    <Button type="button" onClick={() => { setOtpStep(0); setOtpCode(""); }} variant="outline" className="text-xs">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {otpStep === 2 && (
            <div className="p-5 rounded-2xl bg-emerald-100 border border-emerald-300 text-emerald-900" data-testid="otp-verified-box">
              <p className="font-semibold flex items-center gap-2"><SealCheck size={20} weight="fill" /> Aadhaar verified via OTP — you're all set! </p>
              <p className="text-xs mt-1">You don't need to upload ID images below.</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FileUpload label="Aadhaar Front Side *" url={aadhaarFrontUrl} onChange={setAadhaarFrontUrl} testId="upload-aadhaar-front" />
            <FileUpload label="Aadhaar Back Side *" url={aadhaarBackUrl} onChange={setAadhaarBackUrl} testId="upload-aadhaar-back" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FileUpload label="Voter ID (optional)" url={voterUrl} onChange={setVoterUrl} testId="upload-voter" />
          </div>

          <Button onClick={submit} disabled={submitting} className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6" data-testid="submit-verify-btn">
            {submitting ? "Submitting..." : "Submit for Verification"}
          </Button>
        </div>
      )}
    </div>
  );
}
